import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { HighlightStroke, StepHighlights } from "@tebiki/shared";
import { Banner, Btn, Stepper, cautionVariant } from "../components/index.js";
import { Icon } from "../components/Icon.js";
import { Frame, type BBox } from "../components/Frame.js";
import { Highlighter } from "../components/Highlighter.js";
import { MarkdownText } from "../components/MarkdownText.js";
import { LoadingState, ErrorState } from "../components/EmptyState.js";
import { useSurface } from "../lib/useSurface.js";
import { useAsync } from "../lib/useAsync.js";
import { useSelectionWithin } from "../lib/useSelectionWithin.js";
import { manuals, mediaUrl } from "../lib/api.js";
import { mmss, localeLabel, type StepWithCandidates } from "../lib/demo.js";
import { bboxToFrame } from "../lib/bbox.js";

function toBBox(step: StepWithCandidates): BBox | null {
  return bboxToFrame(step.bbox, step.objectLabel);
}

/** Which highlight layer(s) to show: official, personal, or both. */
type LayerMode = "official" | "personal" | "both";

/** Personal highlights use a coral/pink colour distinct from the brand lime. */
const PERSONAL_COLOR = "#FF8FB1";

/** Web Speech synthesis languages per offered locale (fallback: the raw locale code). */
const TTS_LANG: Record<string, string> = {
  ja: "ja-JP",
  en: "en-US",
  zh: "zh-CN",
  "zh-TW": "zh-TW",
  ko: "ko-KR",
  vi: "vi-VN",
  id: "id-ID",
  th: "th-TH",
  ne: "ne-NP",
  my: "my-MM",
  pt: "pt-BR",
  es: "es-ES",
  tl: "fil-PH",
};

/** Screen F — field step viewer (dark) with TTS + per-step navigation. */
export function Viewer() {
  useSurface("field");
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { data, loading, error, offline, reload } = useAsync(() => manuals.get(id), [id]);

  const [idx, setIdx] = useState(0);
  const [speaking, setSpeaking] = useState(false);

  // --- layer toggle state ---
  const [layerMode, setLayerMode] = useState<LayerMode>("both");

  // --- personal marking mode ---
  const [markingMode, setMarkingMode] = useState(false);
  const [personalStrokes, setPersonalStrokes] = useState<HighlightStroke[]>([]);
  const [personalTerms, setPersonalTerms] = useState<string[]>([]);
  const [personalColor, setPersonalColor] = useState<string>(PERSONAL_COLOR);
  const [selText, setSelText] = useState("");
  const [savingPersonal, setSavingPersonal] = useState(false);
  const [personalNotice, setPersonalNotice] = useState<string | null>(null);
  // Body container for cross-OS selection capture while personal-marking is on.
  const bodyRef = useRef<HTMLDivElement>(null);
  useSelectionWithin(bodyRef, markingMode, setSelText);

  // ---- 多言語閲覧: 表示言語の切替（原文=ja、翻訳済みロケールを重ねて表示） ----
  const [viewLocale, setViewLocale] = useState("ja");
  const transList = useAsync(
    async () => (await manuals.translations(id)).translations,
    [id],
  );
  const transDetail = useAsync(
    async () => (viewLocale === "ja" ? null : await manuals.translation(id, viewLocale)),
    [id, viewLocale],
  );

  // Source steps with the selected locale's translation overlaid (text fields only —
  // images / clips / highlights stay from the master). Untranslated steps fall back to 原文.
  const steps = useMemo(() => {
    const src = (data?.steps ?? []) as StepWithCandidates[];
    const t = viewLocale === "ja" ? null : transDetail.data;
    if (!t) return src;
    const by = new Map(t.steps.map((s) => [s.stepId, s]));
    return src.map((s) => {
      const ts = by.get(s.stepId);
      if (!ts) return s;
      return {
        ...s,
        title: ts.title || s.title,
        caption: ts.caption || s.caption,
        supplement: ts.supplement ?? s.supplement,
        objectLabel: ts.objectLabel ?? s.objectLabel,
        cautions: ts.cautions?.length ? ts.cautions : s.cautions,
        table: ts.table ?? s.table,
      };
    });
  }, [data?.steps, transDetail.data, viewLocale]);
  const step = steps[idx];

  // Sync personal highlights from the loaded step when the step changes.
  useEffect(() => {
    if (!step) return;
    setPersonalStrokes(step.personalHighlights?.strokes ?? []);
    setPersonalTerms(step.personalHighlights?.terms ?? []);
    setPersonalNotice(null);
    setSelText("");
  }, [step?.stepId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Video playback
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoSrc = useMemo(
    () => (data?.sourceType === "video" ? mediaUrl(data.media.originalVideoUrl) : null),
    [data?.sourceType, data?.media.originalVideoUrl],
  );

  function seekToStep(s: StepWithCandidates | undefined, play: boolean) {
    const v = videoRef.current;
    if (!v || !s) return;
    const ms = s.clipStartMs ?? s.videoTimecodeMs ?? 0;
    try {
      v.currentTime = Math.max(0, ms / 1000);
      if (play) void v.play().catch(() => {});
    } catch {
      /* metadata not ready yet */
    }
  }

  useEffect(() => {
    if (videoSrc) seekToStep(step, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, videoSrc]);

  function toggleTts() {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      setSpeaking((s) => !s);
      return;
    }
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    if (step) {
      const u = new SpeechSynthesisUtterance(`${step.title}。${step.caption}`);
      u.lang =
        viewLocale === "ja" ? (data?.locale ? TTS_LANG[data.locale] ?? data.locale : "ja-JP") : TTS_LANG[viewLocale] ?? viewLocale;
      u.onend = () => setSpeaking(false);
      window.speechSynthesis.speak(u);
      setSpeaking(true);
    }
  }

  // --- personal highlight helpers ---
  const onPersonalStrokesChange = useCallback((next: HighlightStroke[]) => {
    setPersonalStrokes(next);
  }, []);

  function addPersonalTerm(raw: string) {
    const t = raw.trim();
    if (!t) return;
    setPersonalTerms((prev) => (prev.includes(t) ? prev : [...prev, t]));
    setSelText("");
    if (typeof window !== "undefined") window.getSelection()?.removeAllRanges();
  }

  function removePersonalTerm(t: string) {
    setPersonalTerms((prev) => prev.filter((x) => x !== t));
  }

  function markBodySelection() {
    const sel = typeof window !== "undefined" ? window.getSelection()?.toString() ?? "" : "";
    const text = (sel || selText).trim();
    if (!text) {
      setPersonalNotice("マークするテキストを本文から選択してください。");
      return;
    }
    addPersonalTerm(text);
    setPersonalNotice(null);
  }

  async function savePersonalHighlights() {
    if (!step || !id) return;
    setSavingPersonal(true);
    setPersonalNotice(null);
    const highlights: StepHighlights = { strokes: personalStrokes, terms: personalTerms };
    try {
      await manuals.saveMyHighlights(id, step.stepId, highlights);
      // Reflect onto the loaded data so re-entry shows them.
      if (step) step.personalHighlights = highlights;
      setPersonalNotice("保存しました。");
    } catch {
      setPersonalNotice("保存に失敗しました。接続を確認して再試行してください。");
    } finally {
      setSavingPersonal(false);
    }
  }

  // Effective strokes / terms to render per layer mode.
  const showOfficial = layerMode === "official" || layerMode === "both";
  const showPersonal = layerMode === "personal" || layerMode === "both";

  const officialStrokes = showOfficial ? (step?.highlights?.strokes ?? []) : [];
  const personalStrokesVisible = showPersonal ? personalStrokes : [];

  const officialTerms = showOfficial ? (step?.highlights?.terms ?? []) : [];
  const personalTermsVisible = showPersonal ? personalTerms : [];

  return (
    <div className="shell">
      <header className="appbar">
        <button
          className="btn btn--ghost"
          aria-label="戻る"
          onClick={() => navigate(-1)}
          style={{ minHeight: 56, minWidth: 56, padding: "8px 14px", borderRadius: 12 }}
        >
          <Icon name="chevron" size={22} style={{ transform: "rotate(90deg)" }} />
        </button>
        <div className="grow">
          <div className="kicker">{data?.title ?? "マニュアル"}</div>
          {steps.length ? <Stepper total={steps.length} current={idx + 1} /> : null}
        </div>
        <button
          className="chip"
          aria-pressed={speaking}
          onClick={toggleTts}
          style={{ minHeight: 48, display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          {speaking ? <Icon name="pause" size={16} /> : <Icon name="speaker" size={16} />}
          {speaking ? "読み上げ中" : "読み上げ"}
        </button>
        {/* 表示言語の切替 — 翻訳済みロケールがあれば選択できる（未選択時は原文 ja） */}
        {/* minWidth:0 + shrinkable select: the chip must compress on phones or the appbar
            overflows sideways once translations exist */}
        <label className="chip" style={{ padding: "8px 10px", minHeight: 48, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", minWidth: 0, flexShrink: 1 }}>
          <Icon name="multilingual" size={16} aria-hidden />
          <select
            aria-label="表示言語"
            value={viewLocale}
            onChange={(e) => setViewLocale(e.target.value)}
            style={{ background: "transparent", border: 0, color: "inherit", font: "inherit", cursor: "pointer", maxWidth: "min(150px, 34vw)", minWidth: 0, width: "100%" }}
          >
            <option value="ja">{localeLabel("ja").name}</option>
            {(transList.data ?? []).map((t) => (
              <option key={t.locale} value={t.locale}>
                {localeLabel(t.locale).name}
                {t.isHumanReviewed ? "" : "（AI訳）"}
              </option>
            ))}
          </select>
        </label>
      </header>

      <main className="view-main">
        {loading ? (
          <div style={{ gridColumn: "1 / -1" }}>
            <LoadingState />
          </div>
        ) : error ? (
          <div style={{ gridColumn: "1 / -1" }}>
            <ErrorState message={error} offline={offline} onRetry={reload} />
          </div>
        ) : step ? (
          <>
            {/* ---- LEFT: image / video stage ---- */}
            <div
              className="stage-img"
              style={{ position: "relative", background: "radial-gradient(120% 90% at 50% 30%,#23262f,#0a0b0e)", display: "grid", placeItems: "center", padding: 30 }}
            >
              {videoSrc ? (
                <>
                  <video
                    ref={videoRef}
                    src={videoSrc}
                    controls
                    playsInline
                    preload="metadata"
                    crossOrigin="anonymous"
                    onLoadedMetadata={() => seekToStep(step, false)}
                    style={{ width: "min(78%,520px)", borderRadius: 16, background: "#000", boxShadow: "0 30px 70px -10px rgba(0,0,0,.7)" }}
                  />
                  <button
                    className="statuspill"
                    style={{ position: "absolute", bottom: 18, left: 24, display: "inline-flex", alignItems: "center", gap: 6 }}
                    onClick={() => seekToStep(step, true)}
                  >
                    <Icon name="play" size={16} />
                    動画でこの手順を見る
                  </button>
                </>
              ) : (
                <>
                  <div style={{ position: "relative", width: "min(70%,360px)" }}>
                    <Frame
                      src={mediaUrl(step.imageUrl)}
                      cap={step.videoTimecodeMs != null ? mmss(step.videoTimecodeMs) : undefined}
                      bbox={toBBox(step)}
                      style={{ width: "100%" }}
                    >
                      {/* Official strokes (read-only always) */}
                      {officialStrokes.length > 0 && !markingMode ? (
                        <Highlighter
                          src={null}
                          value={officialStrokes}
                          readOnly
                          style={{ position: "absolute", inset: 0 }}
                        />
                      ) : null}
                      {/* When marking mode is ON, show editable personal layer on top of read-only official */}
                      {markingMode ? (
                        <>
                          {officialStrokes.length > 0 ? (
                            <Highlighter
                              src={null}
                              value={officialStrokes}
                              readOnly
                              style={{ position: "absolute", inset: 0 }}
                            />
                          ) : null}
                          <Highlighter
                            src={null}
                            value={personalStrokes}
                            onChange={onPersonalStrokesChange}
                            color={personalColor}
                            style={{ position: "absolute", inset: 0 }}
                          />
                        </>
                      ) : personalStrokesVisible.length > 0 ? (
                        // Read-only personal strokes (not marking mode)
                        <Highlighter
                          src={null}
                          value={personalStrokesVisible}
                          readOnly
                          style={{ position: "absolute", inset: 0 }}
                        />
                      ) : null}
                    </Frame>
                  </div>
                  {data?.sourceType === "video" ? (
                    <button
                      className="statuspill"
                      style={{ position: "absolute", bottom: 18, left: 24, display: "inline-flex", alignItems: "center", gap: 6 }}
                      onClick={() => navigate(`/timeline/${id}`)}
                    >
                      <Icon name="play" size={16} />
                      動画でこの手順を見る
                    </button>
                  ) : null}
                </>
              )}

              {/* ---- Layer legend (bottom-right of stage) ---- */}
              <div
                style={{ position: "absolute", bottom: 18, right: 18, display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}
                aria-label="マークの凡例"
              >
                {showOfficial ? (
                  <span style={legendPill}>
                    <span style={{ display: "inline-block", width: 18, height: 8, borderRadius: 4, background: "var(--hi)", opacity: 0.85, marginRight: 5 }} aria-hidden="true" />
                    公式
                  </span>
                ) : null}
                {showPersonal ? (
                  <span style={legendPill}>
                    <span style={{ display: "inline-block", width: 18, height: 8, borderRadius: 4, background: PERSONAL_COLOR, opacity: 0.85, marginRight: 5 }} aria-hidden="true" />
                    自分
                  </span>
                ) : null}
              </div>
            </div>

            {/* ---- RIGHT: body / controls ---- */}
            <div className="read">
              {/* Editorial step header: giant outline numeral beside the step kicker + title
                  (lives in the reading column so it never overlaps the photo). */}
              <div className="read-head">
                <span className="read-num" aria-hidden="true">{String(step.order).padStart(2, "0")}</span>
                <div style={{ minWidth: 0 }}>
                  <div className="kicker">STEP {String(step.order).padStart(2, "0")} / {String(steps.length).padStart(2, "0")}</div>
                  <h1 className="jp display read-title">{step.title}</h1>
                </div>
              </div>

              {/* ---- Layer segmented toggle ---- */}
              <div
                role="radiogroup"
                aria-label="表示するマークの種類"
                style={{ display: "flex", flexWrap: "wrap", gap: 4, margin: "12px 0 16px", background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 999, padding: 3, alignSelf: "flex-start", maxWidth: "100%" }}
              >
                {([ ["official", "公式マーク"], ["personal", "自分のマーク"], ["both", "両方"] ] as [LayerMode, string][]).map(([mode, label]) => (
                  <button
                    key={mode}
                    role="radio"
                    aria-checked={layerMode === mode}
                    onClick={() => setLayerMode(mode)}
                    style={{
                      padding: "6px 13px",
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: "var(--font-mono)",
                      letterSpacing: "0.04em",
                      border: 0,
                      cursor: "pointer",
                      background: layerMode === mode ? "var(--ink)" : "transparent",
                      color: layerMode === mode ? "var(--bg)" : "var(--muted)",
                      transition: "background .15s, color .15s",
                      minHeight: 44,
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* ---- Body text with combined highlights ---- */}
              <div
                ref={bodyRef}
                className="jp"
                style={{ userSelect: markingMode ? "text" : undefined, WebkitUserSelect: markingMode ? "text" : undefined }}
              >
                {/* Body rendered as Markdown (newlines / bold / lists / tables), with official
                    terms in the .hl swipe and the reader's personal terms in a coral underline. */}
                <MarkdownText
                  className="jp"
                  style={{ fontSize: 18, lineHeight: 1.85, color: "var(--ink)" }}
                  text={step.caption}
                  terms={officialTerms}
                  personalTerms={personalTermsVisible}
                />
              </div>

              {/* ---- Cautions ---- */}
              {step.cautions.map((c, ci) => (
                <Banner key={ci} variant={cautionVariant(c.level)}>
                  <span>{c.text}</span>
                </Banner>
              ))}

              {/* ---- Table ---- */}
              {step.table ? (
                <table className="tbl" style={{ marginTop: 18 }}>
                  <thead>
                    <tr>
                      {step.table.columns.map((c) => (
                        <th key={c}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {step.table.rows.map((row, ri) => (
                      <tr key={ri}>
                        {row.map((cell, ci) => (
                          <td key={ci} className="jp">{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}

              {/* ---- Personal marking panel ---- */}
              {markingMode ? (
                <div
                  style={{ marginTop: 18, padding: "14px 16px", borderRadius: 14, border: `1.5px solid ${PERSONAL_COLOR}`, background: "color-mix(in srgb, #FF8FB1 8%, var(--surface))" }}
                  aria-label="自分のマーク編集"
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <span className="kicker" style={{ color: PERSONAL_COLOR }}>自分のマーク — 画像</span>
                    <span style={{ flex: 1 }} />
                    <button
                      onClick={() => setPersonalStrokes([])}
                      style={{ fontSize: 12, color: "var(--muted)", border: "1px solid var(--line)", borderRadius: 8, padding: "10px 14px", minHeight: 40, background: "var(--surface)", cursor: "pointer" }}
                      aria-label="画像マークを全消去"
                    >
                      全消去
                    </button>
                    <button
                      onClick={() => setPersonalStrokes((s) => s.slice(0, -1))}
                      disabled={personalStrokes.length === 0}
                      style={{ fontSize: 12, color: "var(--muted)", border: "1px solid var(--line)", borderRadius: 8, padding: "10px 14px", minHeight: 40, background: "var(--surface)", cursor: "pointer" }}
                      aria-label="最後のマークを取り消し"
                    >
                      取り消し
                    </button>
                  </div>

                  {/* Personal ink colour palette */}
                  <div className="row" role="radiogroup" aria-label="自分のマーカーの色" style={{ gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                    {[PERSONAL_COLOR, "#FFE14D", "#7FD4FF", "#FFB258"].map((c) => (
                      <button
                        key={c}
                        role="radio"
                        aria-checked={personalColor === c}
                        aria-label={`色: ${c}`}
                        onClick={() => setPersonalColor(c)}
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          background: c,
                          border: personalColor === c ? "2.5px solid var(--ink)" : "1px solid var(--line-2)",
                          boxShadow: personalColor === c ? `0 0 0 3px color-mix(in srgb, ${c} 35%, transparent)` : undefined,
                          cursor: "pointer",
                          padding: 0,
                        }}
                      />
                    ))}
                    <span style={{ fontSize: 11, color: "var(--muted)", fontFamily: "var(--font-mono)", display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <Icon name="arrow-left" size={14} />
                      画像の上でなぞってマーク
                    </span>
                  </div>

                  <span className="kicker" style={{ color: PERSONAL_COLOR, display: "block", marginBottom: 8 }}>自分のマーク — 本文フレーズ</span>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                    <input
                      type="text"
                      value={selText}
                      placeholder="本文を選択 or フレーズを入力"
                      onChange={(e) => setSelText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") markBodySelection(); }}
                      className="jp"
                      aria-label="自分の重要フレーズ"
                      style={{ flex: "1 1 200px", minHeight: 44, padding: "8px 12px", borderRadius: 10, border: "1px solid var(--line)", background: "var(--surface)", color: "var(--ink)", fontSize: 14 }}
                    />
                    <button
                      onClick={markBodySelection}
                      style={{ minHeight: 44, padding: "8px 16px", borderRadius: 10, background: PERSONAL_COLOR, color: "#10130a", fontWeight: 700, fontSize: 13, border: 0, cursor: "pointer" }}
                    >
                      マーク
                    </button>
                  </div>
                  {personalTerms.length > 0 ? (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                      {personalTerms.map((t) => (
                        <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 999, border: `1px solid ${PERSONAL_COLOR}`, fontSize: 13 }}>
                          <span style={{ borderBottom: `2px solid ${PERSONAL_COLOR}` }}>{t}</span>
                          <button
                            aria-label={`「${t}」のマークを削除`}
                            onClick={() => removePersonalTerm(t)}
                            style={{ border: 0, background: "transparent", color: "var(--muted)", cursor: "pointer", fontSize: 13, lineHeight: 1, display: "inline-flex", alignItems: "center", padding: 12, margin: "-12px -10px -12px -8px" }}
                          >
                            <Icon name="close" size={13} />
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : null}

                  {personalNotice ? (
                    <p style={{ fontSize: 12, color: personalNotice.includes("失敗") ? "var(--danger)" : "var(--ok)", fontFamily: "var(--font-mono)", margin: "6px 0 0" }}>
                      {personalNotice}
                    </p>
                  ) : null}

                  <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                    <button
                      onClick={() => { setMarkingMode(false); setPersonalNotice(null); }}
                      style={{ flex: 1, minHeight: 48, borderRadius: 12, border: "1px solid var(--line)", background: "var(--surface)", color: "var(--muted)", fontWeight: 600, cursor: "pointer" }}
                    >
                      キャンセル
                    </button>
                    <button
                      onClick={savePersonalHighlights}
                      disabled={savingPersonal}
                      style={{ flex: 2, minHeight: 48, borderRadius: 12, background: PERSONAL_COLOR, color: "#10130a", fontWeight: 700, border: 0, cursor: "pointer", fontSize: 15 }}
                    >
                      {savingPersonal ? "保存中…" : "保存"}
                    </button>
                  </div>
                </div>
              ) : null}

              {/* ---- Navigation ---- */}
              <div className="navbtns" style={{ display: "flex", gap: 12, marginTop: "auto", paddingTop: 22 }}>
                <Btn
                  variant="ghost"
                  lg
                  className="grow"
                  disabled={idx === 0}
                  onClick={() => setIdx((i) => Math.max(0, i - 1))}
                  icon={<Icon name="chevron" size={18} style={{ transform: "rotate(90deg)" }} />}
                >
                  前の手順
                </Btn>
                {idx < steps.length - 1 ? (
                  <Btn variant="hi" lg className="grow" onClick={() => setIdx((i) => Math.min(steps.length - 1, i + 1))}>
                    次の手順
                    <Icon name="chevron" size={18} style={{ transform: "rotate(-90deg)", marginLeft: 6, verticalAlign: "middle" }} />
                  </Btn>
                ) : (
                  <Btn variant="hi" lg className="grow" onClick={() => navigate(`/handsfree/${id}`)}>
                    ハンズフリー再生
                    <Icon name="chevron" size={18} style={{ transform: "rotate(-90deg)", marginLeft: 6, verticalAlign: "middle" }} />
                  </Btn>
                )}
              </div>

              {/* ---- Quick links row ---- */}
              <div className="row" style={{ marginTop: 10, gap: 6, flexWrap: "wrap" }}>
                <button
                  className="btn btn--ghost"
                  style={{ ...quickLink, display: "inline-flex", alignItems: "center", gap: 6 }}
                  onClick={() => navigate(`/feedback?manual=${id}&step=${step.stepId}`)}
                >
                  <Icon name="feedback" size={16} />
                  この手順は分かりにくい
                </button>
                <button
                  className="btn btn--ghost"
                  style={{ ...quickLink, display: "inline-flex", alignItems: "center", gap: 6 }}
                  onClick={() => navigate(`/annotate/${id}?step=${step.stepId}`)}
                >
                  <Icon name="frame" size={16} />
                  注釈を編集
                </button>
                <button
                  className="btn btn--ghost"
                  aria-pressed={markingMode}
                  style={{ ...quickLink, display: "inline-flex", alignItems: "center", gap: 6, ...(markingMode ? { color: PERSONAL_COLOR, borderBottom: `2px solid ${PERSONAL_COLOR}` } : {}) }}
                  onClick={() => { setMarkingMode((m) => !m); setPersonalNotice(null); }}
                >
                  <Icon name="edit" size={16} />
                  自分でマーク
                </button>
                <button
                  className="btn btn--ghost"
                  style={{ ...quickLink, display: "inline-flex", alignItems: "center", gap: 6 }}
                  onClick={() => navigate(`/digest/${id}`)}
                >
                  <Icon name="ai-review" size={16} />
                  重要ポイントだけ
                </button>
                <button
                  className="btn btn--ghost"
                  style={{ ...quickLink, display: "inline-flex", alignItems: "center", gap: 6 }}
                  onClick={() => navigate(`/checklist/${id}`)}
                >
                  <Icon name="approval" size={16} />
                  チェックリスト
                </button>
              </div>
            </div>
          </>
        ) : null}
      </main>
    </div>
  );
}

const legendPill: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.1em",
  padding: "3px 8px",
  borderRadius: 999,
  background: "rgba(0,0,0,0.5)",
  color: "rgba(255,255,255,0.7)",
};

const quickLink: React.CSSProperties = {
  border: 0,
  color: "var(--muted)",
  fontSize: 13,
};
